import React, { useState } from 'react';
import { marketplaceApi } from '../../api/marketplace';
import { cardsApi } from '../../api/cards';
import { useLanguage } from '../../i18n/LanguageContext';
import { formatDate } from '../../utils/locale';

const TradeCard = ({ trade, user, onAcceptSuccess, onRejectSuccess, onError, onCounterOfferSuccess }) => {
  const { t } = useLanguage();
  const [processing, setProcessing] = useState(false);
  const [showCounterOfferModal, setShowCounterOfferModal] = useState(false);
  const [showCardSelectionModal, setShowCardSelectionModal] = useState(false);
  const [showOpenTradeCounterOfferModal, setShowOpenTradeCounterOfferModal] = useState(false);
  const [userCards, setUserCards] = useState([]);
  const [loadingCards, setLoadingCards] = useState(false);
  const [selectedCounterCardId, setSelectedCounterCardId] = useState(null);
  const [selectedCardId, setSelectedCardId] = useState(null);
  
  // Check if this is an open trade (has desiredPlayer and desiredRarity, and no card2)
  // An open trade has desiredPlayer/desiredRarity but no specific card2 or user2
  const hasDesiredPlayer = trade.desiredPlayer && (trade.desiredPlayer.id || trade.desiredPlayer.name);
  const hasDesiredRarity = trade.desiredRarity && trade.desiredRarity.trim() !== '';
  const hasNoCard2 = !trade.card2 && !trade.card2Id;
  
  const isOpenTrade = hasDesiredPlayer && hasDesiredRarity && hasNoCard2;
  
  // Debug log for open trades
  if (hasDesiredPlayer || hasDesiredRarity) {
    console.log('[TradeCard] Trade check:', {
      id: trade.id,
      desiredPlayer: trade.desiredPlayer,
      desiredRarity: trade.desiredRarity,
      card2: trade.card2,
      card2Id: trade.card2Id,
      hasDesiredPlayer,
      hasDesiredRarity,
      hasNoCard2,
      isOpenTrade
    });
  }

  const handleAccept = async () => {
    if (!user?.id) {
      onError(t('trade.loginToAccept'));
      return;
    }
    
    // For open trades, user must select which card to use
    if (isOpenTrade) {
      setLoadingCards(true);
      try {
        const response = await cardsApi.getUserCards(user.id, {
          player_id: trade.desiredPlayer.id,
          rarity: trade.desiredRarity
        });
        if (response?.success && response?.cards) {
          const availableCards = response.cards.filter(c => 
            !c.is_in_sale && 
            !c.is_in_auction && 
            !c.is_on_loan &&
            c.player_id === trade.desiredPlayer.id &&
            c.rarity?.name?.toLowerCase() === trade.desiredRarity.toLowerCase()
          );
          if (availableCards.length === 0) {
            onError(t('trade.noRequestedCardAvailable'));
            return;
          }
          if (availableCards.length === 1) {
            // Auto-select if only one card
            setSelectedCardId(availableCards[0].id);
            await acceptTradeWithCard(availableCards[0].id);
          } else {
            // Show selection modal if multiple cards
            setUserCards(availableCards);
            setShowCardSelectionModal(true);
          }
        } else {
          onError(t('trade.noRequestedCard'));
        }
      } catch (error) {
        onError(error.message || t('trade.loadCardsFailed'));
      } finally {
        setLoadingCards(false);
      }
    } else {
      // Traditional trade
      await acceptTradeWithCard();
    }
  };
  
  const acceptTradeWithCard = async (card2Id) => {
    setProcessing(true);
    try {
      await marketplaceApi.acceptTrade(trade.id, user.id, card2Id);
      onAcceptSuccess();
      setShowCardSelectionModal(false);
      setSelectedCardId(null);
    } catch (error) {
      onError(error.message || t('trade.acceptFailed'));
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!user?.id) {
      onError(t('trade.loginToReject'));
      return;
    }
    setProcessing(true);
    try {
      await marketplaceApi.rejectTrade(trade.id, user.id);
      onRejectSuccess();
    } catch (error) {
      onError(error.message || t('trade.rejectFailed'));
    } finally {
      setProcessing(false);
    }
  };

  const handleOpenCounterOffer = async () => {
    if (!user?.id) {
      onError(t('trade.loginToCounter'));
      return;
    }
    setLoadingCards(true);
    try {
      const response = await cardsApi.getUserCards(user.id, {});
      if (response?.success && response?.cards) {
        // Filter out cards that are listed, in use, or the one being traded
        const availableCards = response.cards.filter(c => 
          !c.is_in_sale && 
          !c.is_in_auction && 
          !c.is_on_loan && 
          c.id !== trade.card2Id // Can't counter-offer with the card they're asking for
        );
        setUserCards(availableCards);
        if (availableCards.length === 0) {
          onError(t('trade.noCardsForCounter'));
          return;
        }
        setShowCounterOfferModal(true);
      }
    } catch (error) {
      onError(error.message || t('trade.loadCardsFailed'));
    } finally {
      setLoadingCards(false);
    }
  };

  const handleOpenTradeCounterOffer = async () => {
    if (!user?.id) {
      onError(t('trade.loginToCounter'));
      return;
    }
    setLoadingCards(true);
    try {
      const response = await cardsApi.getUserCards(user.id, {});
      if (response?.success && response?.cards) {
        // Filter out cards that are listed, in use, or already in a trade
        const availableCards = response.cards.filter(c => 
          !c.is_in_sale && 
          !c.is_in_auction && 
          !c.is_on_loan
        );
        setUserCards(availableCards);
        if (availableCards.length === 0) {
          onError(t('trade.noCardsForCounter'));
          return;
        }
        setShowOpenTradeCounterOfferModal(true);
      }
    } catch (error) {
      onError(error.message || t('trade.loadCardsFailed'));
    } finally {
      setLoadingCards(false);
    }
  };

  const handleCreateOpenTradeCounterOffer = async () => {
    if (!selectedCounterCardId || !user?.id || !trade?.id) {
      onError(t('trade.selectCardForCounter'));
      return;
    }
    setProcessing(true);
    try {
      await marketplaceApi.createCounterOffer(trade.id, user.id, selectedCounterCardId);
      setShowOpenTradeCounterOfferModal(false);
      setSelectedCounterCardId(null);
      // Show success message and refresh
      if (onCounterOfferSuccess) {
        onCounterOfferSuccess();
      } else {
        onAcceptSuccess(); // Fallback to refresh the list
      }
    } catch (error) {
      onError(error.message || t('trade.createCounterFailed'));
    } finally {
      setProcessing(false);
    }
  };

  const handleCreateCounterOffer = async () => {
    if (!selectedCounterCardId || !user?.id) {
      onError(t('trade.selectCardForCounter'));
      return;
    }
    setProcessing(true);
    try {
      // Create new trade: user (who received offer) offers their card for the original card offered
      await marketplaceApi.createTrade({
        user1Id: user.id, // User making counter offer
        user2Id: trade.user1Id, // Original offer maker
        card1Id: selectedCounterCardId, // Card user wants to give
        card2Id: trade.card1Id // Card user wants to receive (the one originally offered)
      });
      // Reject the original trade
      await marketplaceApi.rejectTrade(trade.id, user.id);
      onAcceptSuccess(); // Refresh the list
    } catch (error) {
      onError(error.message || t('trade.createCounterFailed'));
    } finally {
      setProcessing(false);
      setShowCounterOfferModal(false);
      setSelectedCounterCardId(null);
    }
  };

  return (
    <div className="football-card glass-football p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-xl font-bold text-[#1e293b] mb-2">
            {isOpenTrade ? t('trade.openTrade') : t('trade.offerFrom', { username: trade.user1Username })}
          </h3>
          <p className="text-sm text-[#64748b]">
            {isOpenTrade && trade.user1Username && `${t('trade.byUser', { username: trade.user1Username })} • `}
            {t('trade.expires', { date: formatDate(trade.expiresAt) })}
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <div className="p-4 bg-blue-500/10 border-2 border-blue-500/30 rounded-lg">
          <p className="text-sm text-[#64748b] mb-1">{t('trade.theyOffer')}:</p>
          <p className="font-semibold text-[#1e293b]">{trade.card1?.playerName || t('trade.unknownPlayer')}</p>
          <p className="text-xs text-[#64748b]">{trade.card1?.rarity || t('trade.unknownRarity')}</p>
        </div>
        <div className="p-4 bg-green-500/10 border-2 border-green-500/30 rounded-lg">
          <p className="text-sm text-[#64748b] mb-1">{isOpenTrade ? `${t('trade.theyWant')}:` : `${t('trade.youGive')}:`}</p>
          {isOpenTrade ? (
            <>
              <p className="font-semibold text-[#1e293b]">{trade.desiredPlayer?.name || t('trade.unknownPlayer')}</p>
              <p className="text-xs text-[#64748b]">⭐ {trade.desiredRarity || t('trade.unknownRarity')}</p>
            </>
          ) : (
            <>
              <p className="font-semibold text-[#1e293b]">{trade.card2?.playerName || t('trade.unknownPlayer')}</p>
              <p className="text-xs text-[#64748b]">{trade.card2?.rarity || t('trade.unknownRarity')}</p>
            </>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {/* Only show buttons if user is not the trade creator */}
        {trade.user1Id !== user?.id ? (
          <>
            <button
              onClick={handleAccept}
              disabled={processing || !user || loadingCards}
              className="btn-football flex-1 bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loadingCards ? t('common.loading') : processing ? t('common.processing') : t('trade.accept')}
            </button>
            {isOpenTrade ? (
              // For open trades: show "Make Counter Offer" button
              <button
                onClick={handleOpenTradeCounterOffer}
                disabled={processing || !user || loadingCards}
                className="px-4 py-3 bg-yellow-600 hover:bg-yellow-700 text-white font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loadingCards ? t('common.loading') : t('trade.makeCounter')}
              </button>
            ) : (
              // For traditional trades: show Counter Offer and Reject buttons
              <>
                <button
                  onClick={handleOpenCounterOffer}
                  disabled={processing || !user || loadingCards}
                  className="px-4 py-3 bg-yellow-600 hover:bg-yellow-700 text-white font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loadingCards ? t('common.loading') : t('trade.counterOffer')}
                </button>
                <button
                  onClick={handleReject}
                  disabled={processing || !user}
                  className="px-6 py-3 border-2 border-red-500 text-red-300 hover:bg-red-500/20 font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('trade.reject')}
                </button>
              </>
            )}
          </>
        ) : (
          // User is the trade creator - show info message
          <div className="w-full p-3 bg-blue-500/10 border-2 border-blue-500/30 rounded-lg text-center">
            <p className="text-[#1e293b] font-medium">{t('trade.ownerHint')}</p>
          </div>
        )}
      </div>

      {/* Card Selection Modal for Open Trades */}
      {showCardSelectionModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="football-card glass-football p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-[#1e293b] mb-4">{t('trade.selectCardTitle')}</h2>
            <div className="mb-4">
              <p className="text-gray-300 mb-2">
                {t('trade.youWillReceive')}: <strong>{trade.card1?.playerName || t('common.unknown')}</strong> ({trade.card1?.rarity})
              </p>
              <p className="text-gray-300 mb-4">
                {t('trade.youWillGive')}: <strong>{trade.desiredPlayer?.name || t('common.unknown')}</strong> (⭐ {trade.desiredRarity})
              </p>
              
              <label className="block text-gray-300 mb-2">{t('trade.selectWhichCard')}</label>
              <div className="max-h-60 overflow-y-auto space-y-2">
                {userCards.map((card) => (
                  <div
                    key={card.id}
                    onClick={() => setSelectedCardId(card.id)}
                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedCardId === card.id
                        ? 'border-green-500 bg-green-500/20'
                        : 'border-gray-600 hover:border-gray-500 bg-gray-800/50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-white">{card.player?.full_name || t('common.unknown')}</p>
                        <p className="text-xs text-gray-400">
                          {card.rarity?.name || t('common.unknown')} • {card.player?.position || t('common.notAvailable')}
                        </p>
                      </div>
                      {selectedCardId === card.id && (
                        <div className="text-green-500">✓</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex space-x-4">
              <button
                onClick={() => acceptTradeWithCard(selectedCardId)}
                disabled={processing || !selectedCardId}
                className="btn-football flex-1 bg-green-600 hover:bg-green-700"
              >
                {processing ? t('common.processing') : t('trade.accept')}
              </button>
              <button
                onClick={() => {
                  setShowCardSelectionModal(false);
                  setSelectedCardId(null);
                }}
                className="px-6 py-3 border-2 border-gray-500 text-gray-300 hover:bg-gray-700 font-semibold rounded-lg transition-all"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Counter Offer Modal for Open Trades */}
      {showOpenTradeCounterOfferModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="football-card glass-football p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold text-[#1e293b] mb-4">{t('trade.makeCounterTitle')}</h2>
            <div className="mb-4">
              <p className="text-gray-300 mb-2">
                {t('trade.theyOffer')}: <strong>{trade.card1?.playerName || t('trade.unknownPlayer')}</strong> ({trade.card1?.rarity || t('trade.unknownRarity')})
              </p>
              <p className="text-gray-300 mb-2">
                {t('trade.theyWant')}: <strong>{trade.desiredPlayer?.name || t('trade.unknownPlayer')}</strong> ({trade.desiredRarity || t('trade.unknownRarity')})
              </p>
              <p className="text-sm text-gray-400 mb-4">
                {t('trade.counterHint')}
              </p>
              
              <label className="block text-white font-semibold mb-2">{t('trade.selectCardToOffer')}</label>
              <select
                value={selectedCounterCardId || ''}
                onChange={(e) => setSelectedCounterCardId(e.target.value ? parseInt(e.target.value) : null)}
                className="dark-select w-full p-3 rounded-lg font-medium focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
              >
                <option value="">{t('trade.selectCardPlaceholder')}</option>
                {userCards.map((card) => (
                  <option key={card.id} value={card.id}>
                    {card.player?.full_name || t('trade.unknownPlayer')} - {card.rarity?.name || t('trade.unknownRarity')}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex gap-2 mt-6">
              <button
                onClick={() => {
                  setShowOpenTradeCounterOfferModal(false);
                  setSelectedCounterCardId(null);
                }}
                className="btn-football flex-1 bg-gray-600 hover:bg-gray-700"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleCreateOpenTradeCounterOffer}
                disabled={!selectedCounterCardId || processing}
                className="btn-football flex-1 bg-yellow-600 hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {processing ? t('trade.sending') : t('trade.sendCounter')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Counter Offer Modal */}
      {showCounterOfferModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="football-card glass-football p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-[#1e293b] mb-4">{t('trade.counterOfferTitle')}</h2>
            <div className="mb-4">
              <p className="text-gray-300 mb-2">
                {t('trade.theyWant')}: <strong>{trade.card2?.playerName || t('trade.yourCard')}</strong>
              </p>
              <p className="text-gray-300 mb-4">
                {t('trade.youWant')}: <strong>{trade.card1?.playerName || t('trade.theirCard')}</strong>
              </p>
              
              <label className="block text-gray-300 mb-2">{t('trade.selectCardInstead')}</label>
              <div className="max-h-60 overflow-y-auto space-y-2">
                {userCards.map((card) => (
                  <div
                    key={card.id}
                    onClick={() => setSelectedCounterCardId(card.id)}
                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedCounterCardId === card.id
                        ? 'border-yellow-500 bg-yellow-500/20'
                        : 'border-gray-600 hover:border-gray-500 bg-gray-800/50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-white">{card.player?.full_name || t('common.unknown')}</p>
                        <p className="text-xs text-gray-400">
                          {card.rarity?.name || t('common.unknown')} • {card.player?.position || t('common.notAvailable')}
                        </p>
                      </div>
                      {selectedCounterCardId === card.id && (
                        <div className="text-yellow-500">✓</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex space-x-4">
              <button
                onClick={handleCreateCounterOffer}
                disabled={processing || !selectedCounterCardId}
                className="btn-football flex-1 bg-yellow-600 hover:bg-yellow-700"
              >
                {processing ? t('trade.creating') : t('trade.sendCounter')}
              </button>
              <button
                onClick={() => {
                  setShowCounterOfferModal(false);
                  setSelectedCounterCardId(null);
                }}
                className="px-6 py-3 border-2 border-gray-500 text-gray-300 hover:bg-gray-700 font-semibold rounded-lg transition-all"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TradeCard;
